import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useGetExpeditionCountWoRefetchBo } from "@/api/expedition.api";
import { ExpeditionProcessStatusEnum } from "@/constants/enums/expeditionProcessStatus.enum";
import type { GetAllExpeditionBoDto } from "@/types/api/api-types";
import { FilterTabs, type FilterTabOption } from "@/components/ui/customs/filter-tabs";
import { deliveryStatusGroupToParams } from "@/components/backOffice/validatedExpeditions/useValidatedExpeditionsFilters.reducer";

export type PackagingTab = "box" | "ready" | "shipped";

export const PACKAGING_TABS: PackagingTab[] = ["box", "ready", "shipped"];

export const BOX_TAB_QUERY_FILTER: GetAllExpeditionBoDto = {
  processStatus: [ExpeditionProcessStatusEnum.PENDING_PELICAN],
  excludeRemote: true,
};

// En cours, hors expéditions déjà scannées par le transporteur : elles passent dans l'onglet "Expédiées"
export const READY_TAB_QUERY_FILTER: GetAllExpeditionBoDto = {
  processStatus: [ExpeditionProcessStatusEnum.VALIDATED],
  ...deliveryStatusGroupToParams("ongoing"),
  excludeFirstScanned: true,
  excludeRemote: true,
};

// Livrées, mais aussi toutes celles déjà scannées par le transporteur (tracking.firstScanAt)
export const SHIPPED_TAB_QUERY_FILTER: GetAllExpeditionBoDto = {
  processStatus: [ExpeditionProcessStatusEnum.VALIDATED],
  ...deliveryStatusGroupToParams("delivered"),
  includeFirstScanned: true,
  excludeRemote: true,
};

const INACTIVE_TAB = "text-gray-500 hover:bg-gray-100";
const INACTIVE_BADGE = "bg-gray-500/10 text-gray-500";

const usePackagingTabOptions = (): FilterTabOption[] => {
  const { t } = useTranslation("boLogistics");
  return useMemo(
    () => [
      {
        label: t("packagingTabs.box"),
        value: "box",
        width: "w-56",
        activeClassName: "bg-[#3562f8] text-white",
        inactiveClassName: INACTIVE_TAB,
        badgeActiveClassName: "bg-white/20 text-white",
        badgeInactiveClassName: INACTIVE_BADGE,
      },
      {
        label: t("packagingTabs.ready"),
        value: "ready",
        width: "w-28",
        activeClassName: "bg-orange-600 text-white",
        inactiveClassName: INACTIVE_TAB,
        badgeActiveClassName: "bg-white/20 text-white",
        badgeInactiveClassName: INACTIVE_BADGE,
      },
      {
        label: t("packagingTabs.shipped"),
        value: "shipped",
        width: "w-28",
        activeClassName: "bg-teal-600 text-white",
        inactiveClassName: INACTIVE_TAB,
        badgeActiveClassName: "bg-white/20 text-white",
        badgeInactiveClassName: INACTIVE_BADGE,
      },
    ],
    [t],
  );
};

type Props = {
  tab: PackagingTab;
  onTabChange: (tab: PackagingTab) => void;
};

export const PackagingStatusBox = ({ tab, onTabChange }: Props) => {
  const tabOptions = usePackagingTabOptions();
  const { data: boxCount } = useGetExpeditionCountWoRefetchBo(BOX_TAB_QUERY_FILTER);
  const { data: readyCount } = useGetExpeditionCountWoRefetchBo(READY_TAB_QUERY_FILTER);
  const { data: shippedCount } = useGetExpeditionCountWoRefetchBo(SHIPPED_TAB_QUERY_FILTER);

  const countsMap = new Map<string | null, number | undefined>([
    ["box", boxCount],
    ["ready", readyCount],
    ["shipped", shippedCount],
  ]);

  return (
    <FilterTabs
      options={tabOptions}
      value={tab}
      onChange={(value) => onTabChange(value as PackagingTab)}
      counts={countsMap}
      className="w-full flex-col border-none !bg-transparent !shadow-none sm:w-fit sm:flex-row"
    />
  );
};
